import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hand, Users, HeartHandshake, ChevronRight, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://customer-assets.emergentagent.com/job_f2410923-45b5-41a3-af61-0c43786e0f4d/artifacts/zxthmtj5_Screenshot%202026-07-09%20at%2010.57.39%E2%80%AFAM.png";

const MODES = [
  { key: "dominant", label: "Dominant Hand", desc: "One hand, one story", icon: Hand },
  { key: "both", label: "Both Hands", desc: "Left and right, in dialogue", icon: Users },
  { key: "couples", label: "Couples", desc: "Two hands, side by side", icon: HeartHandshake },
];

export default function Hub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/practitioner/stats").then(setStats).catch(() => {});
  }, []);

  const firstName = user?.full_name?.split(" ")[0] || "Practitioner";

  return (
    <div className="soma-grain min-h-screen p-6 md:p-10">
      <header className="mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-soma-brand">// hold the container</p>
        <h1 className="mt-1 font-display text-4xl font-medium text-soma-on md:text-5xl">
          Good tidings, {firstName}.
        </h1>
      </header>

      {/* Hero */}
      <div className="relative mb-10 overflow-hidden rounded-3xl border border-soma-border">
        <img src={HERO} alt="" className="h-56 w-full object-cover md:h-72" />
        <div className="absolute inset-0 bg-gradient-to-b from-soma-surface/90 via-soma-surface/20 to-transparent" />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-4 p-5 md:p-7">
          <p className="font-display text-3xl italic leading-none text-soma-on md:text-4xl">Snap the palm.</p>
          <div className="shrink-0 text-right text-sm leading-relaxed text-soma-on2">
            <p>We&apos;ll trace the lines.</p>
            <p>You hold the container.</p>
            <p>Choose a mode to begin.</p>
          </div>
        </div>
      </div>

      {/* Mode selector */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl text-soma-on">Initiate a Reading</h2>
        <button
          onClick={() => navigate("/lore")}
          data-testid="hub-lore-link"
          className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-soma-brand hover:text-soma-on"
        >
          <BookOpen size={13} /> Wander the lore
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {MODES.map((m) => (
          <button
            key={m.key}
            data-testid={`mode-${m.key}`}
            onClick={() => navigate(`/reading/capture?mode=${m.key}`)}
            className="group flex items-center gap-4 rounded-2xl border border-soma-border bg-soma-surface2 p-5 text-left transition-colors hover:border-soma-brand"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-soma-brand/15 text-soma-brand">
              <m.icon size={22} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-soma-on">{m.label}</p>
              <p className="text-xs text-soma-muted">{m.desc}</p>
            </div>
            <ChevronRight size={18} className="text-soma-muted transition-transform group-hover:translate-x-1 group-hover:text-soma-brand" />
          </button>
        ))}
      </div>

      {/* Stats + recent */}
      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="grid grid-cols-2 gap-4 lg:col-span-1">
          <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-5">
            <p className="text-3xl font-semibold text-soma-on">{stats?.readings ?? "—"}</p>
            <p className="text-xs text-soma-muted">Readings</p>
          </div>
          <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-5">
            <p className="text-3xl font-semibold text-soma-on">
              {stats ? `$${(stats.revenue / 100).toFixed(0)}` : "—"}
            </p>
            <p className="text-xs text-soma-muted">Revenue</p>
          </div>
        </div>

        <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-6 lg:col-span-2">
          <h3 className="mb-4 font-display text-xl text-soma-on">Recent readings</h3>
          {!stats?.recent?.length ? (
            <p className="py-6 text-center text-sm text-soma-muted">Ready for the first seeker.</p>
          ) : (
            <div className="divide-y divide-soma-border">
              {stats.recent.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm text-soma-on">{r.client_name || "Anonymous seeker"}</p>
                    <p className="text-xs capitalize text-soma-muted">{r.mode}</p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] ${
                      r.is_paid ? "bg-soma-success/25 text-soma-on" : "bg-soma-surface3 text-soma-muted"
                    }`}
                  >
                    {r.is_paid ? "Paid" : "Free"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
